// src/pages/document/DocumentClauseLibraryPage.tsx
// Contract clause library: browse + filter reusable clauses, move each through
// draft -> approved -> deprecated, and assemble a template from a selection.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import { ErrorAlert, Badge } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatDate } from '@/features/document/logic';
import {
  clauseStatusMap,
  clauseCategoryKeys,
  clauseCategoryLabelKey,
  mapping,
  parseList,
} from '@/features/document/complianceLogic';

interface ClauseForm {
  title: string;
  summary: string;
  jurisdiction: string;
  language: string;
  category: string;
  body: string;
  tags: string;
  variables: string;
  isDefault: boolean;
}

const EMPTY_FORM: ClauseForm = {
  title: '',
  summary: '',
  jurisdiction: '',
  language: '',
  category: '',
  body: '',
  tags: '',
  variables: '',
  isDefault: false,
};

export const DocumentClauseLibraryPage = observer(() => {
  const { t, locale } = useI18n();
  const store = useDocument();
  const scope = useScope();
  const navigate = useNavigate();

  const [clauses, setClauses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ClauseForm>(EMPTY_FORM);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [buildMode, setBuildMode] = useState(false);
  const [buildSel, setBuildSel] = useState<Set<string>>(new Set());
  const [buildName, setBuildName] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    store
      .ListClauses()
      .then((res: any) => setClauses(res?.clauses?.items ?? []))
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  }, [scope.productId, store]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clauses.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (categoryFilter && c.category !== categoryFilter) return false;
      if (q && !(c.title?.toLowerCase().includes(q) || c.body?.toLowerCase().includes(q) || (c.tags || []).some((tag: string) => tag.toLowerCase().includes(q)))) return false;
      return true;
    });
  }, [clauses, search, statusFilter, categoryFilter]);

  const selected = clauses.find((c) => c.entity?.id === selectedId) || null;

  // Load the full clause record on selection (list rows may be summaries).
  const selectClause = useCallback((cid: string) => {
    setSelectedId(cid);
    setEditing(false);
    store
      .GetClause(cid)
      .then((res: any) => {
        const full = res?.clause ?? res;
        if (full?.entity?.id) setClauses((prev) => prev.map((c) => (c.entity?.id === cid ? { ...c, ...full } : c)));
      })
      .catch(() => { /* keep list data */ });
  }, [store]);

  const openCreate = () => {
    setSelectedId(null);
    setForm(EMPTY_FORM);
    setEditing(true);
  };

  const openEdit = (c: any) => {
    setForm({
      title: c.title || '',
      summary: c.summary || '',
      jurisdiction: c.jurisdiction || '',
      language: c.language || '',
      category: c.category || '',
      body: c.body || '',
      tags: (c.tags || []).join(', '),
      variables: (c.variable_names || []).join(', '),
      isDefault: !!c.is_default,
    });
    setEditing(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setBusy('save');
    try {
      if (selected) {
        await store.UpdateClause(selected.entity.id, {
          clause: {
            ...selected,
            title: form.title,
            summary: form.summary,
            jurisdiction: form.jurisdiction,
            language: form.language,
            category: form.category || undefined,
            body: form.body,
            tags: parseList(form.tags),
            variable_names: parseList(form.variables),
            is_default: form.isDefault,
          },
        } as any);
      } else {
        await store.CreateClause({
          entity: { product_id: scope.productId, type_name: 'document.clause' },
          title: form.title,
          summary: form.summary || undefined,
          jurisdiction: form.jurisdiction,
          language: form.language,
          category: form.category || undefined,
          body: form.body,
          tags: parseList(form.tags),
          variable_names: parseList(form.variables),
          is_default: form.isDefault,
        } as any);
      }
      setEditing(false);
      load();
    } catch {
      // handled by store
    } finally {
      setBusy(null);
    }
  };

  const approve = async (c: any) => {
    setBusy('approve');
    try {
      await store.ApproveClause(c.entity.id, { clause: c.entity } as any);
      load();
    } catch { /* store */ } finally { setBusy(null); }
  };

  const deprecate = async (c: any) => {
    setBusy('deprecate');
    try {
      await store.DeprecateClause(c.entity.id, { clause: c.entity } as any);
      load();
    } catch { /* store */ } finally { setBusy(null); }
  };

  const remove = async () => {
    if (!selected) return;
    setBusy('delete');
    try {
      await store.DeleteClause(selected.entity.id);
      setSelectedId(null);
      setConfirmDelete(false);
      load();
    } catch { /* store */ } finally { setBusy(null); }
  };

  const buildTemplate = async () => {
    if (buildSel.size === 0 || !buildName.trim()) return;
    setBusy('build');
    try {
      const first = clauses.find((c) => buildSel.has(c.entity?.id));
      const res: any = await store.BuildTemplateFromClauses({
        entity: { product_id: scope.productId, type_name: 'document_template' },
        name: buildName,
        jurisdiction: first?.jurisdiction || '',
        language: first?.language || '',
        clause_ids: [...buildSel],
        supports_conditional_blocks: false,
        linked_documents: [],
      } as any);
      const tid = res?.template?.entity?.id;
      if (tid) navigate(`/documents/templates/${tid}`);
      else { setBuildMode(false); setBuildSel(new Set()); setBuildName(''); }
    } catch { /* store */ } finally { setBusy(null); }
  };

  const toggleBuild = (cid: string) => {
    setBuildSel((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="sr-only">{t('document.clause.title')}</h1>
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={() => { setBuildMode((v) => !v); setBuildSel(new Set()); }}
            className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${buildMode ? 'border-primary text-primary bg-primary/5' : 'border-border text-foreground hover:bg-muted'}`}
          >
            {t('document.clause.buildTemplate')}
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {t('document.clause.new')}
          </button>
        </div>
      </div>

      {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}

      {buildMode && (
        <div className="mb-4 rounded-lg border border-primary/40 bg-primary/5 p-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground">{t('document.clause.buildSelected', { n: buildSel.size })}</span>
          <input
            type="text"
            value={buildName}
            onChange={(e) => setBuildName(e.target.value)}
            placeholder={t('document.clause.templateName')}
            className="flex-1 min-w-[180px] rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            type="button"
            disabled={buildSel.size === 0 || !buildName.trim() || busy !== null}
            onClick={buildTemplate}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            {busy === 'build' ? t('common.saving') : t('document.clause.build')}
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('document.clause.searchPlaceholder')}
          className="flex-1 min-w-[200px] rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
        />
        <Select value={statusFilter} onChange={(e: any) => setStatusFilter(e.target.value)} placeholder={t('document.clause.allStatuses')} aria-label={t('document.clause.filterStatus')}>
          {Object.keys(clauseStatusMap).map((k) => <option key={k} value={k}>{t(clauseStatusMap[k].labelKey)}</option>)}
        </Select>
        <Select value={categoryFilter} onChange={(e: any) => setCategoryFilter(e.target.value)} placeholder={t('document.clause.allCategories')} aria-label={t('document.clause.filterCategory')}>
          {clauseCategoryKeys.map((k) => <option key={k} value={k}>{t(clauseCategoryLabelKey(k))}</option>)}
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        {/* List */}
        <div>
          {loading ? (
            <div className="space-y-2 animate-pulse" aria-busy="true">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-16 rounded bg-muted" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border py-16 text-center">
              <p className="text-sm text-muted-foreground">{t('document.clause.empty')}</p>
              <p className="mt-1 mb-3 text-sm text-muted-foreground">{t('document.clause.subtitle')}</p>
              <button type="button" onClick={openCreate} className="text-sm text-primary hover:underline">
                {t('document.clause.new')}
              </button>
            </div>
          ) : (
            <ul className="space-y-1.5">
              {filtered.map((c) => {
                const info = mapping(clauseStatusMap, c.status);
                const cid = c.entity?.id;
                return (
                  <li key={cid}>
                    <div
                      className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${selectedId === cid ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}
                    >
                      {buildMode && (
                        <input
                          type="checkbox"
                          checked={buildSel.has(cid)}
                          onChange={() => toggleBuild(cid)}
                          aria-label={t('document.clause.selectForBuild', { title: c.title })}
                          className="rounded border-border"
                        />
                      )}
                      <button type="button" onClick={() => selectClause(cid)} className="min-w-0 flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground truncate">{c.title}</span>
                          {c.is_default && <Badge variant="primary" size="sm">{t('document.clause.default')}</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{c.summary || c.body}</div>
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                          <span>{t(clauseCategoryLabelKey(c.category))}</span>
                          {c.jurisdiction && <span>· {c.jurisdiction}</span>}
                          {c.language && <span>· {c.language}</span>}
                        </div>
                      </button>
                      <Badge variant={info.variant as any} size="sm">{t(info.labelKey)}</Badge>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Detail / editor rail */}
        <div className="lg:sticky lg:top-20 self-start">
          {editing ? (
            <ClauseEditor
              form={form}
              setForm={setForm}
              onSave={save}
              onCancel={() => setEditing(false)}
              saving={busy === 'save'}
              isNew={!selected}
            />
          ) : selected ? (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-base font-semibold text-foreground">{selected.title}</h2>
                <Badge variant={mapping(clauseStatusMap, selected.status).variant as any} size="sm">
                  {t(mapping(clauseStatusMap, selected.status).labelKey)}
                </Badge>
              </div>
              {selected.summary && <p className="text-sm text-muted-foreground">{selected.summary}</p>}
              <div className="rounded-md bg-muted/40 p-3 text-sm text-foreground whitespace-pre-wrap max-h-[240px] overflow-y-auto">{selected.body}</div>
              {(selected.variable_names || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selected.variable_names.map((v: string) => (
                    <span key={v} className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground">{`{{${v}}}`}</span>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground">
                {t('document.clause.version', { n: selected.version_number })}
                {selected.approved_at && ` · ${t('document.clause.approvedOn', { date: formatDate(selected.approved_at, locale) })}`}
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <button type="button" onClick={() => openEdit(selected)} className="px-3 py-1.5 text-sm rounded-md border border-border text-foreground hover:bg-muted transition-colors">
                  {t('common.edit')}
                </button>
                {(selected.status === 'CLAUSE_STATUS_DRAFT' || selected.status === 'CLAUSE_STATUS_REVIEW') && (
                  <button type="button" disabled={busy !== null} onClick={() => approve(selected)} className="px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors">
                    {t('document.clause.approve')}
                  </button>
                )}
                {selected.status === 'CLAUSE_STATUS_APPROVED' && (
                  <button type="button" disabled={busy !== null} onClick={() => deprecate(selected)} className="px-3 py-1.5 text-sm rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 transition-colors">
                    {t('document.clause.deprecate')}
                  </button>
                )}
                <button type="button" onClick={() => setConfirmDelete(true)} className="px-3 py-1.5 text-sm rounded-md text-destructive hover:bg-destructive/10 transition-colors">
                  {t('common.delete')}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              {t('document.clause.selectHint')}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={t('document.clause.deleteTitle')}
        description={t('document.clause.deleteBody')}
        confirmLabel={t('common.delete')}
        destructive
        isConfirming={busy === 'delete'}
        onConfirm={remove}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
});

function ClauseEditor({ form, setForm, onSave, onCancel, saving, isNew }: {
  form: ClauseForm;
  setForm: (f: ClauseForm) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  isNew: boolean;
}) {
  const { t } = useI18n();
  const set = <K extends keyof ClauseForm>(k: K, v: ClauseForm[K]) => setForm({ ...form, [k]: v });
  const inputCls = 'w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring';
  return (
    <div className="rounded-lg border border-border p-4 space-y-2.5">
      <h2 className="text-base font-semibold text-foreground">{isNew ? t('document.clause.new') : t('document.clause.editTitle')}</h2>
      <input className={inputCls} value={form.title} onChange={(e) => set('title', e.target.value)} placeholder={t('document.clause.fieldTitle')} aria-label={t('document.clause.fieldTitle')} />
      <input className={inputCls} value={form.summary} onChange={(e) => set('summary', e.target.value)} placeholder={t('document.clause.fieldSummary')} aria-label={t('document.clause.fieldSummary')} />
      <div className="grid grid-cols-2 gap-2">
        <input className={inputCls} value={form.jurisdiction} onChange={(e) => set('jurisdiction', e.target.value)} placeholder={t('document.clause.fieldJurisdiction')} aria-label={t('document.clause.fieldJurisdiction')} />
        <input className={inputCls} value={form.language} onChange={(e) => set('language', e.target.value)} placeholder={t('document.clause.fieldLanguage')} aria-label={t('document.clause.fieldLanguage')} />
      </div>
      <Select value={form.category} onChange={(e: any) => set('category', e.target.value)} placeholder={t('document.clause.fieldCategory')} aria-label={t('document.clause.fieldCategory')}>
        {clauseCategoryKeys.map((k) => <option key={k} value={k}>{t(clauseCategoryLabelKey(k))}</option>)}
      </Select>
      <textarea className={`${inputCls} resize-y`} rows={6} value={form.body} onChange={(e) => set('body', e.target.value)} placeholder={t('document.clause.fieldBody')} aria-label={t('document.clause.fieldBody')} />
      <input className={inputCls} value={form.variables} onChange={(e) => set('variables', e.target.value)} placeholder={t('document.clause.fieldVariables')} aria-label={t('document.clause.fieldVariables')} />
      <input className={inputCls} value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder={t('document.clause.fieldTags')} aria-label={t('document.clause.fieldTags')} />
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input type="checkbox" checked={form.isDefault} onChange={(e) => set('isDefault', e.target.checked)} className="rounded border-border" />
        {t('document.clause.markDefault')}
      </label>
      <div className="flex gap-2 pt-1">
        <button type="button" disabled={saving || !form.title.trim() || !form.body.trim()} onClick={onSave} className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors">
          {saving ? t('common.saving') : t('common.save')}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm rounded-md border border-border text-foreground hover:bg-muted transition-colors">
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

export default DocumentClauseLibraryPage;
